import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaCheckCircle, FaSignOutAlt, FaPauseCircle, FaPlayCircle, FaClock, FaExclamationCircle, FaCalendarCheck } from "react-icons/fa";
import api from "../../services/api";  
import "./attendance.css";
import LocationTracker from "./LocationTracker";

const CheckInOut = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [todayRecord, setTodayRecord] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [extraTimeMinutes, setExtraTimeMinutes] = useState(0);
  
  const pollingIntervalRef = useRef(null);
  const extraTimeIntervalRef = useRef(null);

  // ✅ Helper to stop all polling
  const stopAllPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (extraTimeIntervalRef.current) {
      clearInterval(extraTimeIntervalRef.current);
      extraTimeIntervalRef.current = null;
    }
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    handleGetTodayStatus();
    
    // ✅ CHANGED: Poll every 60 seconds instead of 30 seconds (2x reduction)
    pollingIntervalRef.current = setInterval(() => handleGetTodayStatus(true), 60000);
    
    return () => {
      clearInterval(timer);
      stopAllPolling();
    };
  }, []);

  // Live extra time counter
  useEffect(() => {
    if (attendanceStatus === "company_checkout_pending" && todayRecord?.company_check_out) {
      const updateExtraTime = () => {
        const companyCheckout = new Date(todayRecord.company_check_out);
        const now = new Date();
        const diffMs = now - companyCheckout;
        const minutes = Math.floor(diffMs / 60000);
        setExtraTimeMinutes(minutes > 0 ? minutes : 0);
      };
      
      updateExtraTime();
      // ✅ CHANGED: Update extra time every 30 seconds instead of 10 seconds
      extraTimeIntervalRef.current = setInterval(updateExtraTime, 30000);
      
      return () => {
        if (extraTimeIntervalRef.current) {
          clearInterval(extraTimeIntervalRef.current);
        }
      };
    }
  }, [attendanceStatus, todayRecord?.company_check_out]);

  const handleGetTodayStatus = async (silent = false) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const response = await api.get("/attendance/get_status", {
        headers: { Authorization: `Bearer ${token}` },
        params: { _t: new Date().getTime() }
      });
    
      if (response.status === 200) {
        const newStatus = response.data.status;
        const newRecord = response.data.todayRecord;
        
        // ✅ Stop polling if checked out
        if (newStatus === "checked_out" || newStatus === "not_checked_in") {
          stopAllPolling();
        }
        
        // Notify on status change
        if (attendanceStatus === "checked_in" && newStatus === "company_checkout_pending") {
          setSuccessMsg("Company checkout done! Confirm your checkout to claim extra time.");
          setTimeout(() => setSuccessMsg(""), 5000);
        }
        
        setTodayRecord(newRecord);
        setAttendanceStatus(newStatus);
        if (response.data.extra_time_minutes) {
          setExtraTimeMinutes(response.data.extra_time_minutes);
        }
      }
    } catch (error) {
      if (!silent) console.log("Error:", error);
    }
  };

  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
          () => reject(new Error("Please allow location access")),
          { enableHighAccuracy: true }
        );
      }
    });
  };

  const handleCheckIn = async () => {
    setErrorMsg("");
    try {
      setIsLoading(true);
      const location = await getUserLocation();
      const token = localStorage.getItem("token");
      
      const response = await api.post("/attendance/check_in", 
        { lat: location.lat, lng: location.lng },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.status === 201) {
        setTodayRecord(response.data.record);
        setAttendanceStatus("checked_in");
        setSuccessMsg("Check-in successful!");
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || "Check-in failed");
      setTimeout(() => setErrorMsg(""), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  // NORMAL CHECKOUT - For early leaving before company checkout
  const handleCheckOut = async () => {
    setErrorMsg("");
    setSuccessMsg("");
    
    try {
      setIsLoading(true);
      const location = await getUserLocation();
      const token = localStorage.getItem("token");
      
      const response = await api.post(
        "/attendance/check_out",
        { lat: location.lat, lng: location.lng },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.status === 200) {
        setTodayRecord(response.data.record);
        setAttendanceStatus("checked_out");
        setSuccessMsg("Normal Check-out successful!");
        setTimeout(() => setSuccessMsg(""), 3000);
        
        // ✅ Stop all polling after checkout
        stopAllPolling();
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || "Check-out failed");
      setTimeout(() => setErrorMsg(""), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  // CONFIRM CHECKOUT - Employee extra time ke baad confirm karta hai
  const handleConfirmCheckout = async () => {
    setErrorMsg("");
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      
      const response = await api.post("/attendance/confirm_extra_time", {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200) {
        setTodayRecord(response.data.record);
        setAttendanceStatus("checked_out");
        setExtraTimeMinutes(response.data.extra_time_minutes);
        setSuccessMsg(`Checkout confirmed! Extra time: ${response.data.total_extra_time}`);
        setTimeout(() => setSuccessMsg(""), 5000);
        
        // ✅ Stop all polling after checkout
        stopAllPolling();
      }
    } catch (error) {
      console.error("Error:", error);
      setErrorMsg(error.response?.data?.message || "Failed to confirm");
      setTimeout(() => setErrorMsg(""), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Helper: Calculate working minutes between two dates
  const getWorkingMinutes = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    return Math.floor((end - start) / (1000 * 60));
  };

  // ✅ Helper: Calculate actual working minutes (excluding pause time)
  const getActualWorkingMinutes = () => {
    if (!todayRecord?.check_in) return 0;
    const checkIn = todayRecord.check_in;
    const checkOut = todayRecord.check_out || new Date().toISOString();
    const totalMinutes = getWorkingMinutes(checkIn, checkOut);
    const pauseMinutes = todayRecord.total_pause_minutes || 0;
    return Math.max(0, totalMinutes - pauseMinutes);
  };

  // ✅ Helper: Check if timer is currently paused
  const isTimerPaused = () => {
    return todayRecord?.pause_start_time && !todayRecord?.check_out;
  };

  // ✅ Helper: Get current pause duration if paused
  const getCurrentPauseDuration = () => {
    if (!todayRecord?.pause_start_time) return 0;
    const pauseStart = new Date(todayRecord.pause_start_time);
    const now = new Date();
    return Math.floor((now - pauseStart) / 60000);
  };

  const formatTime = (date) => date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const formatDate = (date) => date.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const safeFormatTime = (dateString) => {
    if (!dateString) return "—";
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? "—" : d.toLocaleTimeString();
  };
  const formatDuration = (mins) => {
    if (mins <= 0) return "0m";
    const h = Math.floor(mins / 60), m = mins % 60;
    return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${m}m`;
  };
  const formatTime12Hour = (timeString) => {
    if (!timeString) return "—";
    const [h, m] = timeString.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  return (
    <div className="checkin-container">
      <div className="header-banner mb-4">
        <div className="header-content">
          <div>
            <h1 className="header-title">Attendance Portal </h1>
            <p className="header-subtitle">Manage your daily check-in and check-out</p>
          </div>
        </div>
      </div>

      {successMsg && <div className="success-message"><FaCheckCircle style={{ marginRight: "6px" }} />{successMsg}</div>}
      {errorMsg && <div className="error-message"><FaExclamationCircle style={{ marginRight: "6px" }} />{errorMsg}</div>}

      <div className="checkin-grid">
        <div className="time-card">
          <div className="time-display">
            <div className="current-date">{formatDate(currentTime)}</div>
            <div className="current-time">{formatTime(currentTime)}</div>
            {todayRecord?.expected_leaving && (
              <div className="expected-leaving-info">
                <span className="expected-label" style={{color: "black"}}>Expected Leaving</span>
                <span className="expected-time">{formatTime12Hour(todayRecord.expected_leaving)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="action-card">
          <div className="card-header-custom">
            <div>
              <h3 className="card-title">Today's Attendance</h3>
              <p className="card-subtitle">{todayRecord ? "Your record for today" : "No record yet"}</p>
            </div>
          </div>

          {todayRecord && (
            <div className="attendance-details">
              <div className="detail-row">
                <div className="detail-item">
                  <span className="detail-label">Check In</span>
                  <span className="detail-value check-in-time">{safeFormatTime(todayRecord.check_in)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Company Checkout</span>
                  <span className="detail-value" style={{ color: '#f59e0b' }}>
                    {todayRecord.company_check_out ? safeFormatTime(todayRecord.company_check_out) : "—"}
                  </span>
                </div>
              </div>
              
              {/* ✅ Pause Timer Status */}
              {isTimerPaused() && (
                <div className="detail-row" style={{ 
                  background: '#fef2f2', 
                  borderRadius: '8px', 
                  padding: '10px 14px',
                  margin: '8px 0',
                  border: '1px solid #fecaca'
                }}>
                  <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                    <span className="detail-label" style={{ color: '#dc2626' }}><FaPauseCircle style={{ marginRight: "6px" }} /> Timer Paused (Outside Office)</span>
                    <span className="detail-value" style={{ color: '#dc2626', fontSize: '14px' }}>
                      Paused for {formatDuration(getCurrentPauseDuration())}
                    </span>
                  </div>
                </div>
              )}

              {/* ✅ Working Hours & Break Time */}
              {attendanceStatus !== "not_checked_in" && (
                <div className="detail-row" style={{ 
                  background: '#f0fdf4', 
                  borderRadius: '8px', 
                  padding: '10px 14px',
                  margin: '8px 0',
                  border: '1px solid #bbf7d0'
                }}>
                  <div className="detail-item">
                    <span className="detail-label"><FaCheckCircle style={{ marginRight: "6px" }} /> Actual Working Hours</span>
                    <span className="detail-value" style={{ color: '#10b981', fontSize: '16px', fontWeight: '700' }}>
                      {formatDuration(getActualWorkingMinutes())}
                    </span>
                  </div>
                  {todayRecord.total_pause_minutes > 0 && (
                    <div className="detail-item">
                      <span className="detail-label">Total Pause Time</span>
                      <span className="detail-value" style={{ color: '#f59e0b' }}>
                        -{formatDuration(todayRecord.total_pause_minutes)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Gross Time (Check In to Now/Check Out) */}
              {attendanceStatus !== "not_checked_in" && (
                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">Gross Time (Including Breaks)</span>
                    <span className="detail-value" style={{ color: '#64748b', fontSize: '13px' }}>
                      {formatDuration(getWorkingMinutes(todayRecord.check_in, todayRecord.check_out || new Date().toISOString()))}
                    </span>
                  </div>
                </div>
              )}
              
              {todayRecord.check_out && todayRecord.check_out_confirmed == 1 && (
                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">Final Check Out</span>
                    <span className="detail-value check-out-time">{safeFormatTime(todayRecord.check_out)}</span>
                  </div>
                  {todayRecord.extra_time_minutes > 0 && (
                    <div className="detail-item">
                      <span className="detail-label">Extra Time</span>
                      <span className="detail-value" style={{ color: '#10b981' }}>
                        +{formatDuration(todayRecord.extra_time_minutes)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="detail-row">
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className="status-badge status-present">
                    {attendanceStatus === "checked_in" && !isTimerPaused() && <><FaPlayCircle style={{ marginRight: '6px' }} />Working</>}
                    {attendanceStatus === "checked_in" && isTimerPaused() && <><FaPauseCircle style={{ marginRight: '6px' }} />Paused (Outside)</>}
                    {attendanceStatus === "company_checkout_pending" && "⏳ Confirm Checkout"}
                    {attendanceStatus === "checked_out" && "Completed"}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="action-buttons" style={{display: 'flex', flexDirection: "column", alignItems: "center", paddintTop: '4px'}}>
            {/* CHECK IN BUTTON */}
            {(!attendanceStatus || attendanceStatus === "not_checked_in") && (
              <button className="btn-submit btn-checkin" onClick={handleCheckIn} disabled={true}>
                {isLoading ? "Processing..." : <><FaPlayCircle style={{ marginRight: "6px" }} />Check In</>}
              </button>
            )}

            {/* WORKING - Before company checkout, normal check out available */}
            {attendanceStatus === "checked_in" && (
              <button
                className="btn-submit btn-checkout"
                onClick={handleCheckOut}
                disabled={true}
              >
                {isLoading ? "Processing..." : <><FaSignOutAlt style={{ marginRight: "6px" }} />Normal Check Out</>}
              </button>
            )}
            
            <span style={{colro: 'grey', marginTop: '10px'}}>For Check In & Check Out , Login your App</span>

            {/* ✅ CONFIRM CHECKOUT BUTTON - After company checkout */}
            {attendanceStatus === "company_checkout_pending" && (
              <div className="extra-time-confirmation">
                <div className="company-checkout-banner">
                  <span className="banner-icon"></span>
                  <div>
                    <h4>Company Checkout Done</h4>
                    <p>at {safeFormatTime(todayRecord.company_check_out)}</p>
                  </div>
                </div>

                <div className="extra-time-live-counter">
                  <div className="counter-label"><FaClock style={{ marginRight: "6px" }} />Extra Time</div>
                  <div className="counter-value">+{formatDuration(extraTimeMinutes)}</div>
                </div>

                <button
                  className="btn-submit btn-confirm-extra-time"
                  onClick={handleConfirmCheckout}
                  disabled={isLoading}
                  type="button"
                >
                  {isLoading ? "Processing..." : <><FaCheckCircle style={{ marginRight: "6px" }} />Confirm Checkout (+{formatDuration(extraTimeMinutes)} extra)</>}
                </button>
              </div>
            )}

            {/* COMPLETED */}
            {attendanceStatus === "checked_out" && (
              <div className="completed-message">
                <div>
                  <p>Attendance completed for today!</p>
                  <small style={{ display: 'block', color: '#10b981', fontWeight: '600' }}>
                    Actual Working: {formatDuration(getActualWorkingMinutes())}
                  </small>
                  {todayRecord?.total_pause_minutes > 0 && (
                    <small style={{ display: 'block', color: '#f59e0b' }}>
                      <FaPauseCircle style={{ marginRight: '6px' }} />Break Time: {formatDuration(todayRecord.total_pause_minutes)}
                    </small>
                  )}
                  {todayRecord?.extra_time_minutes > 0 && (
                    <small style={{ display: 'block', color: '#8b5cf6' }}>
                      <FaClock style={{ marginRight: '6px' }} />Extra time: +{formatDuration(todayRecord.extra_time_minutes)}
                    </small>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <LocationTracker />
    </div>
  );
};

export default CheckInOut;